import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Play, Sparkles, Pause, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang, useAuth } from "@/context/AppContext";
import { PageHead, PrimaryBtn, GhostBtn } from "@/pages/app/Dashboard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi } from "@/lib/useApi";

const ACTIONS = ["share_contact", "schedule_interview", "send_offer", "sign_contract"];
const DATA = ["profile", "contact", "cv", "references"];
const blank = () => ({ principal_id: "", title: "", skills: "", sector: "", location: "", remote: false, compensation: 0, openings: 1, source: "manual",
  policy: { min_trust: 0, require_verified: false, allowed_sectors: "", allowed_actions: ["share_contact", "schedule_interview"], shared_data: ["profile"] } });

const Chips = ({ options, value, onChange, prefix, testPrefix }) => {
  const { t } = useLang();
  const toggle = (o) => onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o]);
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button type="button" key={o} onClick={() => toggle(o)} data-testid={`${testPrefix}-${o}`}
          className={`rounded-full border px-3 py-1 text-xs transition-colors duration-200 ${value.includes(o) ? "border-emerald-400 bg-emerald-400/10 text-emerald-300" : "border-white/10 text-slate-400"}`}>{t(`${prefix}_${o}`)}</button>
      ))}
    </div>
  );
};

const AIAssist = ({ kind, onApply }) => {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [proposal, setProposal] = useState(null);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/ai/propose-intent", { text, kind, lang }); setProposal(data.proposal); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!user.ai_consent) return <p className="rounded-md border border-white/10 p-3 text-xs text-slate-400" data-testid="ai-needs-consent">{t("ai_needs_consent")}</p>;
  return (
    <div className="space-y-3 rounded-md border border-emerald-400/20 bg-emerald-400/[0.03] p-4" data-testid="ai-assist-panel">
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-emerald-400"><Sparkles className="h-3.5 w-3.5" />{t("ai_assist")}</p>
      <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={t("ai_placeholder")} data-testid="ai-intent-text" />
      <GhostBtn type="button" disabled={busy || text.length < 5} onClick={run} data-testid="ai-generate-btn">{busy ? "…" : t("ai_generate")}</GhostBtn>
      {proposal && (
        <div className="space-y-2 rounded-md border border-yellow-400/30 bg-[#0B0F17] p-3 text-xs" data-testid="ai-proposal">
          <p className="font-mono uppercase tracking-widest text-yellow-300">{t("ai_proposal")}</p>
          <p className="text-sm text-white">{proposal.title}</p>
          <p className="text-slate-400">{proposal.skills.join(", ")} · {proposal.location} · {proposal.compensation}€</p>
          {proposal.rationale && <p className="italic text-slate-500">{proposal.rationale}</p>}
          <PrimaryBtn type="button" onClick={() => { onApply(proposal); setProposal(null); toast.info(t("ai_applied")); }} data-testid="ai-apply-btn">{t("ai_apply")}</PrimaryBtn>
        </div>
      )}
    </div>
  );
};

const IntentForm = ({ principals, onDone }) => {
  const { t } = useLang();
  const [f, setF] = useState(blank());
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setP = (k, v) => setF({ ...f, policy: { ...f.policy, [k]: v } });
  const kind = principals.find((p) => p.id === f.principal_id)?.kind;
  const apply = (p) => setF({ ...f, ...p, skills: p.skills.join(", "), source: "ai_confirmed", policy: { ...p.policy, allowed_sectors: p.policy.allowed_sectors.join(", ") } });

  const submit = async (e) => {
    e.preventDefault();
    const split = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
    const body = { ...f, compensation: Number(f.compensation) || 0, openings: Number(f.openings) || 1, skills: split(f.skills),
      policy: { ...f.policy, min_trust: Number(f.policy.min_trust) || 0, allowed_sectors: split(f.policy.allowed_sectors), counterpart_kinds: [kind === "person" ? "company" : "person"] } };
    delete body.rationale;
    try { await api.post("/intents", body); onDone(); } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="intent-form">
      <Select value={f.principal_id} onValueChange={(v) => setF({ ...f, principal_id: v })}>
        <SelectTrigger data-testid="intent-principal-select"><SelectValue placeholder={t("principal")} /></SelectTrigger>
        <SelectContent className="bg-[#111827]">
          {principals.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`intent-principal-option-${p.id}`}>{p.name} · {t(p.kind)}</SelectItem>)}
        </SelectContent>
      </Select>
      {kind && <AIAssist kind={kind} onApply={apply} />}
      <Input required placeholder={t("title")} value={f.title} onChange={set("title")} data-testid="intent-title-input" />
      <Input placeholder={t("skills")} value={f.skills} onChange={set("skills")} data-testid="intent-skills-input" />
      <div className="grid grid-cols-2 gap-3">
        <Input placeholder={t("sector")} value={f.sector} onChange={set("sector")} data-testid="intent-sector-input" />
        <Input placeholder={t("location")} value={f.location} onChange={set("location")} data-testid="intent-location-input" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="space-y-1 text-xs text-slate-400">{t(kind === "company" ? "comp_company" : "comp_person")}<Input type="number" min={0} value={f.compensation} onChange={set("compensation")} data-testid="intent-compensation-input" /></label>
        {kind === "company" && <label className="space-y-1 text-xs text-slate-400">{t("openings")}<Input type="number" min={1} value={f.openings} onChange={set("openings")} data-testid="intent-openings-input" /></label>}
      </div>
      <label className="flex items-center gap-3 text-sm text-slate-300"><Switch checked={f.remote} onCheckedChange={(v) => setF({ ...f, remote: v })} data-testid="intent-remote-switch" />{t("remote")}</label>
      <div className="space-y-3 rounded-md border border-white/10 p-4">
        <p className="font-mono text-[11px] uppercase tracking-widest text-cyan-300">{t("policy")}</p>
        <label className="block space-y-1 text-xs text-slate-400">{t("min_trust")}<Input type="number" min={0} max={100} value={f.policy.min_trust} onChange={(e) => setP("min_trust", e.target.value)} data-testid="policy-min-trust-input" /></label>
        <label className="flex items-center gap-3 text-sm text-slate-300"><Switch checked={f.policy.require_verified} onCheckedChange={(v) => setP("require_verified", v)} data-testid="policy-require-verified-switch" />{t("require_verified")}</label>
        <Input placeholder={t("allowed_sectors")} value={f.policy.allowed_sectors} onChange={(e) => setP("allowed_sectors", e.target.value)} data-testid="policy-sectors-input" />
        <p className="text-xs text-slate-400">{t("allowed_actions")}</p>
        <Chips options={ACTIONS} value={f.policy.allowed_actions} onChange={(v) => setP("allowed_actions", v)} prefix="act" testPrefix="policy-action" />
        <p className="text-xs text-slate-400">{t("shared_data")}</p>
        <Chips options={DATA} value={f.policy.shared_data} onChange={(v) => setP("shared_data", v)} prefix="data" testPrefix="policy-data" />
      </div>
      <PrimaryBtn type="submit" disabled={!f.principal_id} data-testid="intent-submit-btn">{t("new_intent")}</PrimaryBtn>
    </form>
  );
};

const MatchDialog = ({ result, onClose }) => {
  const { t } = useLang();
  return (
    <Dialog open={!!result} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto border-white/10 bg-[#111827] sm:max-w-2xl" data-testid="match-results-dialog">
        <DialogHeader><DialogTitle className="font-display">{t("match_results")}</DialogTitle><DialogDescription className="sr-only">AYDORIQ</DialogDescription></DialogHeader>
        {result && (
          <div className="space-y-3">
            <p className="font-mono text-xs text-slate-400" data-testid="match-summary">{result.evaluated} {t("evaluated")} · {result.proposed} {t("proposed_n")}</p>
            {result.results.map((r, i) => (
              <div key={`${r.counterpart_intent}-${r.counterpart}-${r.relationship_id || i}`} className={`rounded-md border p-4 ${r.eligible ? "border-emerald-400/30" : "border-rose-400/20"}`} data-testid={`match-result-${i}`}>
                <div className="flex items-center justify-between">
                  <div><p className="font-semibold text-white">{r.counterpart}</p><p className="text-xs text-slate-400">{r.counterpart_intent}</p></div>
                  <div className="text-right"><p className="font-display text-2xl font-bold text-emerald-300">{r.score}</p>
                    <p className={`font-mono text-[10px] uppercase tracking-widest ${r.eligible ? "text-emerald-400" : "text-rose-400"}`}>{t(r.eligible ? "eligible" : "not_eligible")}</p></div>
                </div>
                {!!r.violations.length && <p className="mt-2 font-mono text-[11px] text-rose-300">{r.violations.join(" · ")}</p>}
                {r.relationship_id && <Link to={`/app/connections/${r.relationship_id}`} className="mt-2 inline-block text-xs text-cyan-300 hover:underline" data-testid={`match-view-connection-${i}`}>{t("view_connection")} →</Link>}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function Intents() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [match, setMatch] = useState(null);
  const { data: items, reload: loadIntents } = useApi("/intents", { initial: [] });
  const { data: principals, reload: loadPrincipals } = useApi("/principals", { initial: [] });
  const load = () => Promise.all([loadIntents(), loadPrincipals()]);
  const name = (id) => principals.find((p) => p.id === id)?.name || "—";
  const runMatch = async (id) => { try { const { data } = await api.post(`/engine/match/${id}`); setMatch(data); } catch (e) { toast.error(errMsg(e)); } };
  const setStatus = async (id, status) => { try { await api.patch(`/intents/${id}`, { status }); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div>
      <PageHead title={t("app_intents")}>
        <PrimaryBtn onClick={() => setOpen(true)} data-testid="declare-intent-btn"><Plus className="h-4 w-4" />{t("new_intent")}</PrimaryBtn>
      </PageHead>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-white/10 bg-[#111827] sm:max-w-xl">
          <DialogHeader><DialogTitle className="font-display">{t("new_intent")}</DialogTitle><DialogDescription className="sr-only">AYDORIQ</DialogDescription></DialogHeader>
          <IntentForm principals={principals} onDone={() => { setOpen(false); load(); }} />
        </DialogContent>
      </Dialog>
      <MatchDialog result={match} onClose={() => { setMatch(null); load(); }} />
      {!items.length && <p className="text-sm text-slate-500" data-testid="intents-empty">{t("empty")}</p>}
      <div className="space-y-3">
        {items.map((i) => (
          <div key={i.id} className="flex flex-wrap items-center justify-between gap-4 rounded-md border border-white/[0.08] bg-[#111827] p-5" data-testid={`intent-row-${i.id}`}>
            <div className="min-w-0">
              <p className="font-display text-lg font-bold text-white">{i.title}</p>
              <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-slate-500">{name(i.principal_id)} · {t(i.principal_kind)} · {i.skills.join(", ") || "—"} · {i.compensation}€ {i.source === "ai_confirmed" && "· AI✓"}</p>
              <p className="mt-1 text-xs text-slate-400">{t("granted_actions")}: {i.policy.allowed_actions.map((a) => t(`act_${a}`)).join(", ")}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-[11px] uppercase tracking-widest text-slate-400" data-testid={`intent-status-${i.id}`}>{i.status}</span>
              {i.status === "active" && <GhostBtn onClick={() => setStatus(i.id, "paused")} data-testid={`intent-pause-${i.id}`}><Pause className="h-3.5 w-3.5" /></GhostBtn>}
              {i.status === "paused" && <GhostBtn onClick={() => setStatus(i.id, "active")} data-testid={`intent-resume-${i.id}`}><RotateCcw className="h-3.5 w-3.5" /></GhostBtn>}
              <PrimaryBtn disabled={i.status !== "active"} onClick={() => runMatch(i.id)} data-testid={`run-match-btn-${i.id}`}><Play className="h-3.5 w-3.5" />{t("run_match")}</PrimaryBtn>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
